import React, { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';

import { areEqual } from 'Utils/equalityChecks';
import { Api } from 'Utils/api';

import { ReferralPartnerModal } from '../ReferralPartnerModal';
import { Option, PipelineOption, CustomFieldConfig, LeadFormData, StageOption, FormFieldSetting } from '../types';

import classes from './newLeadForm.module.css';

interface NewLeadFormProps {
  locationId: string;
  ssoToken: string;
  referralPartnerOptions: Option[];
  pipelineOptions: PipelineOption[];
  companyOptions: Option[];
  customFieldConfig: CustomFieldConfig;
  formFieldSettings: FormFieldSetting[];
  isLoadingContacts: boolean;
  isLoadingCompanies: boolean;
  isLoadingPipelines: boolean;
  pipelineStorageKey: string | null;
  onReferralPartnerCreated: (partner: Option) => void;
  onCompanyCreated: (company: Option) => void;
  onLeadCreated: (lead: Option) => void;
}

const defaultFormValues: LeadFormData = {
  locationId: '',
  firstName: '',
  lastName: '',
  email: '',
  dialCode: '+1',
  phone: '',
  address: '',
  city: '',
  state: '',
  postalCode: '',
  referredBy: '',
  referredById: '',
  typeOfLoss: '',
  callerClass: '',
  notes: '',
  pipelineId: '',
  stageId: '',
  monetaryValue: '',
  opportunityName: '',
  leadValue: '',
  office: '',
  extraFields: {},
};

function NewLeadFormComponent({
  locationId,
  ssoToken,
  referralPartnerOptions,
  pipelineOptions,
  companyOptions,
  customFieldConfig,
  formFieldSettings,
  isLoadingContacts,
  isLoadingCompanies,
  isLoadingPipelines,
  pipelineStorageKey,
  onReferralPartnerCreated,
  onCompanyCreated,
  onLeadCreated,
}: NewLeadFormProps) {
  const [contactOnly, setContactOnly] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showReferralPartnerModal, setShowReferralPartnerModal] = useState(false);
  const [selectedReferralPartnerLabel, setSelectedReferralPartnerLabel] = useState('');
  const [referralPartnerSearchTerm, setReferralPartnerSearchTerm] = useState('');
  const [isReferralInputFocused, setIsReferralInputFocused] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    watch,
    formState: { errors, isDirty, isValid },
  } = useForm<LeadFormData>({
    mode: 'onChange',
    defaultValues: defaultFormValues,
  });

  const hasPipelines = pipelineOptions.length > 0;
  const selectedPipelineId = watch('pipelineId');

  // Get stages for the selected pipeline
  const stageOptions: StageOption[] = useMemo(() => {
    if (!selectedPipelineId) return [];
    const selectedPipeline = pipelineOptions.find((p) => p.id === selectedPipelineId);
    return selectedPipeline?.stages || [];
  }, [pipelineOptions, selectedPipelineId]);

  // Load stored pipeline selection and auto-select first stage
  useEffect(() => {
    if (pipelineStorageKey && hasPipelines) {
      const storedPipelineId = localStorage.getItem(pipelineStorageKey);
      if (storedPipelineId && pipelineOptions.some((p) => p.id === storedPipelineId)) {
        setValue('pipelineId', storedPipelineId, { shouldValidate: true });
        // Auto-select first stage
        const pipeline = pipelineOptions.find((p) => p.id === storedPipelineId);
        if (pipeline?.stages?.length) {
          setValue('stageId', pipeline.stages[0].id, { shouldValidate: true });
        }
      }
    }
  }, [pipelineStorageKey, hasPipelines, pipelineOptions, setValue]);

  // Filter referral partner options based on search (show all if no search term)
  const filteredReferralPartners = referralPartnerSearchTerm
    ? referralPartnerOptions.filter((option) =>
        option.label.toLowerCase().includes(referralPartnerSearchTerm.toLowerCase())
        || (option.companyName && option.companyName.toLowerCase().includes(referralPartnerSearchTerm.toLowerCase()))
      )
    : referralPartnerOptions;

  const handleReferralPartnerSelect = useCallback((option: Option) => {
    setValue('referredById', option.id);
    setValue('referredBy', option.label);
    setSelectedReferralPartnerLabel(option.companyName ? `${option.label} - ${option.companyName}` : option.label);
    setReferralPartnerSearchTerm('');
  }, [setValue]);

  const handleClearReferralPartner = useCallback(() => {
    setValue('referredById', '');
    setValue('referredBy', '');
    setSelectedReferralPartnerLabel('');
    setReferralPartnerSearchTerm('');
  }, [setValue]);

  const handleReferralPartnerCreated = useCallback((partner: Option) => {
    onReferralPartnerCreated(partner);
    setValue('referredById', partner.id);
    setValue('referredBy', partner.label);
    setSelectedReferralPartnerLabel(partner.companyName ? `${partner.label} - ${partner.companyName}` : partner.label);
    setShowReferralPartnerModal(false);
  }, [onReferralPartnerCreated, setValue]);

  const handleToggleContactOnly = useCallback((isContactOnly: boolean) => {
    setContactOnly(isContactOnly);
    if (isContactOnly) {
      setValue('pipelineId', '', { shouldValidate: true });
      setValue('stageId', '', { shouldValidate: true });
      setValue('opportunityName', '', { shouldValidate: true });
      setValue('monetaryValue', '', { shouldValidate: true });
      setValue('typeOfLoss', '', { shouldValidate: true });
      setValue('callerClass', '', { shouldValidate: true });
      setValue('notes', '', { shouldValidate: true });
      setValue('leadValue', '', { shouldValidate: true });
      setValue('office', '', { shouldValidate: true });
      setValue('extraFields', {}, { shouldValidate: false });
    }
  }, [setValue]);

  const handlePipelineChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    setValue('pipelineId', value, { shouldValidate: true });
    if (pipelineStorageKey) {
      if (value) {
        localStorage.setItem(pipelineStorageKey, value);
      } else {
        localStorage.removeItem(pipelineStorageKey);
      }
    }
    // Auto-select first stage when pipeline changes
    if (value) {
      const selectedPipeline = pipelineOptions.find((p) => p.id === value);
      if (selectedPipeline?.stages?.length) {
        setValue('stageId', selectedPipeline.stages[0].id, { shouldValidate: true });
      } else {
        setValue('stageId', '', { shouldValidate: true });
      }
    } else {
      setValue('stageId', '', { shouldValidate: true });
    }
  }, [setValue, pipelineStorageKey, pipelineOptions]);

  const onSubmit = handleSubmit(async (data) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      // Build custom fields array
      const customFields: { id: string; field_value: string }[] = [];

      if (data.typeOfLoss && customFieldConfig.typeOfLossId) {
        customFields.push({ id: customFieldConfig.typeOfLossId, field_value: data.typeOfLoss });
      }
      if (data.callerClass && customFieldConfig.classId) {
        customFields.push({ id: customFieldConfig.classId, field_value: data.callerClass });
      }
      if (data.notes && customFieldConfig.notesId) {
        customFields.push({ id: customFieldConfig.notesId, field_value: data.notes });
      }
      if (data.referredById && customFieldConfig.contactReferredById) {
        customFields.push({ id: customFieldConfig.contactReferredById, field_value: data.referredById });
      }
      if (data.referredBy && customFieldConfig.contactReferredBy) {
        customFields.push({ id: customFieldConfig.contactReferredBy, field_value: data.referredBy });
      }
      if (data.leadValue && customFieldConfig.leadValueId) {
        customFields.push({ id: customFieldConfig.leadValueId, field_value: data.leadValue });
      }
      if (data.office && customFieldConfig.officeId) {
        customFields.push({ id: customFieldConfig.officeId, field_value: data.office });
      }

      // Dynamic custom fields from settings (split by prefix)
      const contactExtraFields: { id: string; field_value: string }[] = [];
      const opportunityExtraFields: { id: string; field_value: string }[] = [];

      Object.entries(data.extraFields || {}).forEach(([fieldId, value]) => {
        if (value) {
          const setting = formFieldSettings.find((s) => s.fieldId === fieldId);
          if (setting) {
            const fieldEntry = { id: setting.fieldId, field_value: value as string };
            if (setting.fieldKey.startsWith('contact.')) {
              contactExtraFields.push(fieldEntry);
            } else if (setting.fieldKey.startsWith('opportunity.')) {
              opportunityExtraFields.push(fieldEntry);
            }
          }
        }
      });

      customFields.push(...contactExtraFields);

      // Capitalize names
      const firstName = data.firstName.charAt(0).toUpperCase() + data.firstName.slice(1);
      const lastName = data.lastName.charAt(0).toUpperCase() + data.lastName.slice(1);
      const defaultName = `${firstName} ${lastName}`.trim() || 'New Referral';
      const opportunityName = data.opportunityName?.trim() || defaultName;

      // Create contact payload (backend expects snake_case)
      const contactPayload: any = {
        first_name: firstName,
        last_name: lastName,
        location_id: locationId,
        type: 'lead',
        custom_fields: customFields,
      };

      if (data.email?.trim()) {
        contactPayload.email = data.email;
      }
      if (data.phone?.trim()) {
        contactPayload.phone = `${data.dialCode} ${data.phone}`;
      }
      if (data.address?.trim()) {
        contactPayload.address1 = data.address;
      }
      if (data.city?.trim()) {
        contactPayload.city = data.city;
      }
      if (data.state?.trim()) {
        contactPayload.state = data.state;
      }
      if (data.postalCode?.trim()) {
        contactPayload.postal_code = data.postalCode;
      }

      // Set source: referral partner name + company if selected, otherwise default
      if (selectedReferralPartnerLabel) {
        contactPayload.source = selectedReferralPartnerLabel;
      } else {
        contactPayload.source = 'RocketCRM Lead Form';
      }

      // Create contact
      const contactResponse = await Api.post('/ghl/embed/contacts', contactPayload, {
        headers: { 'X-GHL-SSO': ssoToken },
      });

      // Response format: { data: { id: "...", ... } }
      const newContactId = contactResponse?.data?.data?.id || contactResponse?.data?.id;
      if (!newContactId) {
        throw new Error('Failed to create contact');
      }

      // Notify parent about new lead
      onLeadCreated({
        id: newContactId,
        label: opportunityName,
      });

      // Create opportunity if not contact-only mode and pipeline selected
      if (!contactOnly && data.pipelineId && hasPipelines) {
        // Backend expects snake_case field names
        const opportunityPayload: any = {
          pipeline_id: data.pipelineId,
          pipeline_stage_id: data.stageId,
          name: opportunityName,
          status: 'open',
          contact_id: newContactId,
        };

        if (data.referredBy) {
          opportunityPayload.source = data.referredBy;
        }

        if (data.monetaryValue) {
          opportunityPayload.monetary_value = parseFloat(data.monetaryValue);
        }

        if (data.referredById) {
          opportunityPayload.referral_partner_id = data.referredById;
        }

        if (data.typeOfLoss) {
          opportunityPayload.type_of_loss = data.typeOfLoss;
        }

        if (data.callerClass) {
          opportunityPayload.caller_class = data.callerClass;
        }

        if (data.notes) {
          opportunityPayload.notes = data.notes;
        }

        if (data.leadValue) {
          opportunityPayload.lead_value = data.leadValue;
        }

        if (data.office) {
          opportunityPayload.office = data.office;
        }

        if (opportunityExtraFields.length > 0) {
          opportunityPayload.custom_fields = opportunityExtraFields;
        }

        await Api.post('/ghl/embed/opportunities', opportunityPayload, {
          headers: { 'X-GHL-SSO': ssoToken },
        });
      }

      setSuccessMessage(contactOnly ? 'Contact created successfully!' : 'Lead created successfully!');

      // Reset form after delay
      setTimeout(() => {
        setSuccessMessage(null);
        reset(defaultFormValues);
        setSelectedReferralPartnerLabel('');
        // Re-apply stored pipeline and first stage
        if (pipelineStorageKey && hasPipelines) {
          const storedPipelineId = localStorage.getItem(pipelineStorageKey);
          const pipeline = pipelineOptions.find((p) => p.id === storedPipelineId);
          if (storedPipelineId && pipeline) {
            setValue('pipelineId', storedPipelineId, { shouldValidate: true });
            if (pipeline.stages?.length) {
              setValue('stageId', pipeline.stages[0].id, { shouldValidate: true });
            }
          }
        }
      }, 3000);
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Failed to create lead', error);
      const message = error?.response?.data?.message || error?.message || 'Failed to create lead';
      const matchingField = error?.response?.data?.meta?.matchingField;
      const duplicateContactName = error?.response?.data?.meta?.contactName;
      const isDuplicateContact = typeof message === 'string' && message.toLowerCase().includes('duplicated contacts');

      if (isDuplicateContact) {
        setErrorMessage(`A contact${duplicateContactName ? ` for ${duplicateContactName}` : ''} already exists in RocketCRM for this location.`);
      } else {
        setErrorMessage(`${message}${matchingField ? ` Matching ${matchingField} already exists.` : ''}`);
      }
    } finally {
      setIsSubmitting(false);
    }
  });

  return (
    <div className={classes.formContainer}>
      {errorMessage && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {errorMessage}
          <button type="button" className="btn-close" onClick={() => setErrorMessage(null)} aria-label="Close" />
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success alert-dismissible fade show" role="alert">
          {successMessage}
          <button type="button" className="btn-close" onClick={() => setSuccessMessage(null)} aria-label="Close" />
        </div>
      )}

      <div className="btn-group w-100 mb-3" role="group">
        <button
          type="button"
          className={`btn ${!contactOnly ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => handleToggleContactOnly(false)}
        >
          Contact + Opportunity
        </button>
        <button
          type="button"
          className={`btn ${contactOnly ? 'btn-primary' : 'btn-outline-primary'}`}
          onClick={() => handleToggleContactOnly(true)}
        >
          Contact Only
        </button>
      </div>

      <form onSubmit={onSubmit}>
        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="firstName" className="form-label">
              First Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
              id="firstName"
              {...register('firstName', { required: 'First name is required' })}
            />
            {errors.firstName && <div className="invalid-feedback">{errors.firstName.message}</div>}
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="lastName" className="form-label">
              Last Name <span className="text-danger">*</span>
            </label>
            <input
              type="text"
              className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
              id="lastName"
              {...register('lastName', { required: 'Last name is required' })}
            />
            {errors.lastName && <div className="invalid-feedback">{errors.lastName.message}</div>}
          </div>
        </div>

        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="email" className="form-label">Email</label>
            <input
              type="email"
              className={`form-control ${errors.email ? 'is-invalid' : ''}`}
              id="email"
              {...register('email', {
                pattern: {
                  value: /^\S+@\S+$/i,
                  message: 'Invalid email format',
                },
              })}
            />
            {errors.email && <div className="invalid-feedback">{errors.email.message}</div>}
          </div>

          <div className="col-md-6 mb-3">
            <label htmlFor="phone" className="form-label">Phone</label>
            <div className="input-group">
              <select className="form-select" style={{ maxWidth: '80px' }} {...register('dialCode')}>
                <option value="+1">+1</option>
                <option value="+44">+44</option>
                <option value="+61">+61</option>
              </select>
              <input
                type="tel"
                className={`form-control ${errors.phone ? 'is-invalid' : ''}`}
                id="phone"
                placeholder="555-555-5555"
                {...register('phone', {
                  pattern: {
                    value: /^\s*(?:\+?(\d{1,3}))?[-. (]*(\d{3})[-. )]*(\d{3})[-. ]*(\d{4})(?: *x(\d+))?\s*$/,
                    message: 'Invalid phone format',
                  },
                })}
              />
            </div>
            {errors.phone && <div className="text-danger small">{errors.phone.message}</div>}
          </div>
        </div>

        <div className="mb-3">
          <label htmlFor="address" className="form-label">Address</label>
          <input type="text" className="form-control" id="address" {...register('address')} />
        </div>

        <div className="row">
          <div className="col-md-4 mb-3">
            <label htmlFor="city" className="form-label">City</label>
            <input type="text" className="form-control" id="city" {...register('city')} />
          </div>
          <div className="col-md-4 mb-3">
            <label htmlFor="state" className="form-label">State</label>
            <input type="text" className="form-control" id="state" {...register('state')} />
          </div>
          <div className="col-md-4 mb-3">
            <label htmlFor="postalCode" className="form-label">Zip Code</label>
            <input type="text" className="form-control" id="postalCode" {...register('postalCode')} />
          </div>
        </div>

        {/* Referred By - Autocomplete with add button (always visible) */}
        <div className="mb-3">
          <label htmlFor="referredBy" className="form-label">Referred By</label>
          <div className="input-group">
            <div className={`form-control ${classes.autocompleteWrapper}`}>
              {selectedReferralPartnerLabel ? (
                <div className={classes.selectedValue}>
                  <span>{selectedReferralPartnerLabel}</span>
                  <button type="button" className="btn-close btn-sm" onClick={handleClearReferralPartner} aria-label="Clear" />
                </div>
              ) : (
                <>
                  <input
                    type="text"
                    className={classes.autocompleteInput}
                    placeholder="Search for contacts..."
                    value={referralPartnerSearchTerm}
                    onChange={(e) => setReferralPartnerSearchTerm(e.target.value)}
                    onFocus={() => setIsReferralInputFocused(true)}
                    onBlur={() => setTimeout(() => setIsReferralInputFocused(false), 200)}
                  />
                  {isReferralInputFocused && filteredReferralPartners.length > 0 && (
                    <ul className={classes.autocompleteDropdown}>
                      {filteredReferralPartners.slice(0, 10).map((option) => (
                        <li key={option.id}>
                          <button type="button" onClick={() => handleReferralPartnerSelect(option)}>
                            {option.label}
                            {option.companyName && (
                              <span className={classes.companyName}> - {option.companyName}</span>
                            )}
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {isLoadingContacts && <div className={classes.loadingIndicator}>Loading...</div>}
                </>
              )}
            </div>
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setShowReferralPartnerModal(true)}
              title="Add new referral partner"
            >
              +
            </button>
          </div>
        </div>

        {!contactOnly && (
          <>
            {/* Pipeline */}
            <div className="mb-3">
              <label htmlFor="pipelineId" className="form-label">
                Pipeline <span className="text-danger">*</span>
              </label>
              <Controller
                name="pipelineId"
                control={control}
                rules={{ required: !contactOnly ? 'Pipeline is required' : false }}
                render={({ field }) => (
                  <select
                    className={`form-select ${errors.pipelineId ? 'is-invalid' : ''}`}
                    id="pipelineId"
                    disabled={!hasPipelines || isLoadingPipelines}
                    value={field.value}
                    onChange={handlePipelineChange}
                  >
                    <option value="">Select a pipeline...</option>
                    {pipelineOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                )}
              />
              {errors.pipelineId && <div className="invalid-feedback">{errors.pipelineId.message}</div>}
            </div>

            {/* Pipeline Stage */}
            {selectedPipelineId && stageOptions.length > 0 && (
              <div className="mb-3">
                <label htmlFor="stageId" className="form-label">
                  Pipeline Stage <span className="text-danger">*</span>
                </label>
                <Controller
                  name="stageId"
                  control={control}
                  rules={{ required: selectedPipelineId && stageOptions.length > 0 ? 'Pipeline stage is required' : false }}
                  render={({ field }) => (
                    <select
                      className={`form-select ${errors.stageId ? 'is-invalid' : ''}`}
                      id="stageId"
                      value={field.value}
                      onChange={(e) => setValue('stageId', e.target.value, { shouldValidate: true })}
                    >
                      {stageOptions.map((stage) => (
                        <option key={stage.id} value={stage.id}>
                          {stage.label}
                        </option>
                      ))}
                    </select>
                  )}
                />
                {errors.stageId && <div className="invalid-feedback">{errors.stageId.message}</div>}
              </div>
            )}

            {/* Opportunity Name */}
            <div className="mb-3">
              <label htmlFor="opportunityName" className="form-label">Opportunity Name</label>
              <input
                type="text"
                className="form-control"
                id="opportunityName"
                placeholder="Defaults to lead name if left blank"
                {...register('opportunityName')}
              />
            </div>

            {/* Opportunity Value */}
            <div className="mb-3">
              <label htmlFor="monetaryValue" className="form-label">Opportunity Value</label>
              <div className="input-group">
                <span className="input-group-text">$</span>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control"
                  id="monetaryValue"
                  placeholder="0.00"
                  {...register('monetaryValue', {
                    pattern: {
                      value: /^\d*\.?\d{0,2}$/,
                      message: 'Enter a valid dollar amount',
                    },
                  })}
                />
              </div>
              {errors.monetaryValue && <div className="text-danger small">{errors.monetaryValue.message}</div>}
            </div>

            {/* Type of Loss */}
            {customFieldConfig.typeOfLossOptions.length > 0 && (
              <div className="mb-3">
                <label htmlFor="typeOfLoss" className="form-label">Type of Loss</label>
                <select className="form-select" id="typeOfLoss" {...register('typeOfLoss')}>
                  {customFieldConfig.typeOfLossOptions.map((option) => (
                    <option key={option} value={option}>
                      {option || 'Select...'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Caller Classification */}
            {customFieldConfig.classOptions.length > 0 && (
              <div className="mb-3">
                <label htmlFor="callerClass" className="form-label">Caller Classification</label>
                <select className="form-select" id="callerClass" {...register('callerClass')}>
                  {customFieldConfig.classOptions.map((option) => (
                    <option key={option} value={option}>
                      {option || 'Select...'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Notes */}
            <div className="mb-3">
              <label htmlFor="notes" className="form-label">Notes</label>
              <textarea
                className="form-control"
                id="notes"
                rows={3}
                placeholder="Additional information can go here..."
                {...register('notes')}
              />
            </div>

            {/* Lead Value */}
            {customFieldConfig.leadValueId && (
              <div className="mb-3">
                <label htmlFor="leadValue" className="form-label">Lead Value</label>
                {customFieldConfig.leadValueOptions.length > 0 ? (
                  <select className="form-select" id="leadValue" {...register('leadValue')}>
                    {customFieldConfig.leadValueOptions.map((option) => (
                      <option key={option} value={option}>
                        {option || 'Select...'}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input type="text" className="form-control" id="leadValue" {...register('leadValue')} />
                )}
              </div>
            )}

            {/* Office */}
            {customFieldConfig.officeOptions.length > 0 && (
              <div className="mb-3">
                <label htmlFor="office" className="form-label">Office</label>
                <select className="form-select" id="office" {...register('office')}>
                  {customFieldConfig.officeOptions.map((option) => (
                    <option key={option} value={option}>
                      {option || 'Select...'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Dynamic Custom Fields */}
            {formFieldSettings
              .filter((s) => s.applyTo === 'new' || s.applyTo === 'both')
              .map((setting) => (
                <div key={setting.fieldId} className="mb-3">
                  <label htmlFor={setting.fieldId} className="form-label">{setting.label}</label>
                  {setting.picklistOptions.length > 0 ? (
                    <select className="form-select" id={setting.fieldId} {...register(`extraFields.${setting.fieldId}`)}>
                      <option value="">Select...</option>
                      {setting.picklistOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  ) : setting.dataType === 'LARGE_TEXT' ? (
                    <textarea
                      className="form-control"
                      id={setting.fieldId}
                      rows={3}
                      {...register(`extraFields.${setting.fieldId}`)}
                    />
                  ) : (
                    <input
                      type="text"
                      className="form-control"
                      id={setting.fieldId}
                      {...register(`extraFields.${setting.fieldId}`)}
                    />
                  )}
                </div>
              ))}
          </>
        )}

        <button
          type="submit"
          className="btn btn-primary w-100"
          disabled={!isDirty || !isValid || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Creating...
            </>
          ) : (
            contactOnly ? 'Create Contact' : 'Create Lead Contact'
          )}
        </button>
      </form>

      {/* Referral Partner Modal */}
      <ReferralPartnerModal
        isOpen={showReferralPartnerModal}
        onClose={() => setShowReferralPartnerModal(false)}
        locationId={locationId}
        ssoToken={ssoToken}
        companyOptions={companyOptions}
        customFieldConfig={customFieldConfig}
        isLoadingCompanies={isLoadingCompanies}
        onReferralPartnerCreated={handleReferralPartnerCreated}
        onCompanyCreated={onCompanyCreated}
      />
    </div>
  );
}

const NewLeadFormMemo = memo(NewLeadFormComponent, areEqual);

export { NewLeadFormMemo as NewLeadForm };
